import { BanknoteIcon, BriefcaseIcon, Building2Icon, CpuIcon, FactoryIcon, HeartPulseIcon, ScaleIcon, ShoppingBagIcon, TruckIcon, ZapIcon } from 'lucide-react';
import type { Industry } from '../types/content';

export const industries: Industry[] = [
  {
    slug: 'financial-services',
    name: 'Financial Services and Banking',
    short: 'Leadership for banks, asset managers, insurers, and financial institutions.',
    intro:
      'Financial institutions operate under close regulatory scrutiny while competing on technology, customer experience, and trust. Their leaders must balance prudence with the ability to drive change.',
    challenges: ['Regulatory expectations shaping leadership requirements', 'Digital competition from new market entrants', 'Risk, compliance, and conduct oversight at senior level'],
    functions: ['Executive leadership', 'Finance and treasury', 'Risk and compliance', 'Technology and operations', 'Commercial and client leadership'],
    roles: ['Chief Executive Officer', 'Chief Risk Officer', 'Head of Retail Banking', 'Chief Compliance Officer'],
    support:
      'A structured search helps institutions identify leaders whose experience fits both the regulatory context and the organization’s commercial ambitions.',
    icon: BanknoteIcon,
  },
  {
    slug: 'technology',
    name: 'Technology and Digital Transformation',
    short: 'Leaders who build products, platforms, and digitally enabled organizations.',
    intro:
      'Technology now sits at the heart of almost every organization’s strategy. Leaders in this space must combine technical credibility with commercial judgment and the ability to lead change.',
    challenges: ['Scaling engineering and product organizations', 'Leading transformation in established businesses', 'Balancing innovation with security and resilience'],
    functions: ['Technology leadership', 'Product and engineering', 'Data and analytics', 'Information security', 'Digital transformation'],
    roles: ['Chief Technology Officer', 'Chief Digital Officer', 'VP Engineering', 'Chief Information Security Officer'],
    support:
      'Executive search helps organizations look beyond job titles to find leaders who have navigated comparable scale, complexity, and change.',
    icon: CpuIcon,
  },
  {
    slug: 'healthcare',
    name: 'Healthcare and Life Sciences',
    short: 'Leadership for providers, life sciences companies, and health innovators.',
    intro:
      'Healthcare and life sciences organizations combine scientific, clinical, regulatory, and commercial complexity. Their leaders carry responsibility for outcomes that matter deeply to patients and communities.',
    challenges: ['Navigating regulatory and quality requirements', 'Bridging scientific and commercial leadership', 'Leading through funding and market cycles'],
    functions: ['Executive and general management', 'Medical and clinical leadership', 'Regulatory and quality', 'Commercial and market access', 'Operations'],
    roles: ['Chief Medical Officer', 'Head of Regulatory Affairs', 'General Manager', 'Chief Commercial Officer'],
    support:
      'A careful search can identify leaders with the right blend of domain expertise, regulatory awareness, and leadership capability.',
    icon: HeartPulseIcon,
  },
  {
    slug: 'energy-infrastructure',
    name: 'Energy and Infrastructure',
    short: 'Leaders for energy, utilities, renewables, and major infrastructure.',
    intro:
      'Energy and infrastructure organizations manage long-term assets, large capital programmes, and an accelerating transition toward new energy sources. Leadership must combine operational discipline with long-range vision.',
    challenges: ['Leading through the energy transition', 'Delivering complex capital projects', 'Safety, regulatory, and community expectations'],
    functions: ['Executive leadership', 'Project and asset management', 'Operations and HSE', 'Commercial and strategy', 'Finance'],
    roles: ['Chief Operating Officer', 'Projects Director', 'Head of Renewables', 'HSE Director'],
    support:
      'Executive search helps identify leaders with the technical depth and stakeholder judgment that long-horizon organizations require.',
    icon: ZapIcon,
  },
  {
    slug: 'manufacturing',
    name: 'Manufacturing and Industrial',
    short: 'Operational and commercial leaders for industrial organizations.',
    intro:
      'Manufacturers and industrial businesses compete on efficiency, quality, and resilience while adopting automation and responding to shifting supply chains.',
    challenges: ['Modernising operations and adopting automation', 'Building resilient supply chains', 'Attracting leaders to specialist sectors'],
    functions: ['General management', 'Operations and supply chain', 'Engineering', 'Commercial and sales', 'Quality'],
    roles: ['Managing Director', 'Operations Director', 'Plant Director', 'Supply Chain Director'],
    support:
      'A structured search can reach operational leaders in adjacent sectors whose experience transfers well to your environment.',
    icon: FactoryIcon,
  },
  {
    slug: 'professional-services',
    name: 'Professional and Consulting Services',
    short: 'Leaders and partners for advisory, consulting, and professional firms.',
    intro:
      'Professional services firms depend on reputation, relationships, and expertise. Leadership roles often combine client responsibility with firm management.',
    challenges: ['Balancing client delivery with firm leadership', 'Building practices in new markets', 'Retaining and developing senior talent'],
    functions: ['Firm leadership', 'Practice leadership', 'Operations and finance', 'Business development', 'People and talent'],
    roles: ['Managing Partner', 'Practice Head', 'Chief Operating Officer', 'Director of Business Development'],
    support:
      'Executive search helps firms identify leaders whose expertise, relationships, and values align with the firm’s culture.',
    icon: BriefcaseIcon,
  },
  {
    slug: 'consumer-retail',
    name: 'Consumer, Retail, and E-commerce',
    short: 'Leaders for brands, retailers, and digital commerce businesses.',
    intro:
      'Consumer-facing businesses move quickly, responding to changing behaviour, channels, and expectations. Their leaders must understand customers deeply and act decisively.',
    challenges: ['Integrating physical and digital channels', 'Responding to fast-changing consumer behaviour', 'Margin pressure and supply volatility'],
    functions: ['Executive leadership', 'Marketing and brand', 'E-commerce and digital', 'Merchandising and supply', 'Customer experience'],
    roles: ['Chief Marketing Officer', 'Head of E-commerce', 'Retail Director', 'Chief Customer Officer'],
    support:
      'A focused search can find leaders who combine brand instinct with commercial and operational discipline.',
    icon: ShoppingBagIcon,
  },
  {
    slug: 'transportation-logistics',
    name: 'Transportation and Logistics',
    short: 'Leaders for logistics, freight, mobility, and transport networks.',
    intro:
      'Transportation and logistics organizations run complex, time-critical networks. Leadership requires operational precision, safety focus, and an eye on technology-led change.',
    challenges: ['Network efficiency and cost control', 'Technology and data-driven operations', 'Safety and regulatory compliance'],
    functions: ['Executive leadership', 'Network operations', 'Fleet and asset management', 'Commercial', 'Technology'],
    roles: ['Chief Operating Officer', 'Logistics Director', 'Head of Network Planning', 'Fleet Director'],
    support:
      'Executive search can identify leaders who have managed comparable operational complexity and scale.',
    icon: TruckIcon,
  },
  {
    slug: 'legal-governance-risk',
    name: 'Legal, Governance, Risk, and Compliance',
    short: 'General counsel, company secretaries, and risk and compliance leaders.',
    intro:
      'Legal, governance, and risk leaders protect the organization while enabling it to pursue its ambitions. These roles increasingly sit close to the board and executive team.',
    challenges: ['Expanding regulatory obligations', 'Board-level governance expectations', 'Balancing protection with commercial enablement'],
    functions: ['Legal', 'Company secretarial', 'Risk management', 'Compliance', 'Internal audit'],
    roles: ['General Counsel', 'Company Secretary', 'Chief Risk Officer', 'Head of Compliance'],
    support:
      'A discreet search helps organizations find leaders whose judgment and integrity match the trust these roles require.',
    icon: ScaleIcon,
  },
  {
    slug: 'corporate-affairs-policy',
    name: 'Corporate Affairs, Regulatory Strategy, and Public Policy',
    short: 'Leaders who manage reputation, regulation, and stakeholder relationships.',
    intro:
      'Corporate affairs and policy leaders shape how organizations engage with regulators, governments, media, and communities — often during moments of significant scrutiny.',
    challenges: ['Navigating complex regulatory environments', 'Protecting reputation during change', 'Engaging a wide range of stakeholders'],
    functions: ['Corporate affairs', 'Regulatory strategy', 'Public policy and government relations', 'Communications', 'Sustainability'],
    roles: ['Director of Corporate Affairs', 'Head of Regulatory Strategy', 'Head of Public Policy', 'Chief Communications Officer'],
    support:
      'Executive search helps identify leaders with the credibility, judgment, and relationships these sensitive roles demand.',
    icon: Building2Icon,
  },
];
